import React, { useCallback, useMemo, useState } from "react";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../global/directory";
import {
  DONATION_HISTORY_COLUMN_DEFS,
  DONATION_HISTORY_VISIBILITY_KEY,
  DONOR_DIRECTORY_PAGE_SIZE,
  buildValueOptionsFromRows,
  createInitialColumnVisibility,
  defaultDirectoryFilters,
  filterDirectoryRows,
  historyCellValue,
  historySearchableText,
  paginateList,
} from "../utils/donorPortalDirectoryHelpers";

export default function DonationHistoryDirectorySection({ rows, loading, error }) {
  const [filters, setFilters] = useState(defaultDirectoryFilters);
  const [page, setPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    createInitialColumnVisibility(
      DONATION_HISTORY_VISIBILITY_KEY,
      DONATION_HISTORY_COLUMN_DEFS
    )
  );

  const filtered = useMemo(
    () =>
      filterDirectoryRows(rows, filters, {
        getSearchableText: historySearchableText,
        getColumnValue: historyCellValue,
      }),
    [rows, filters]
  );

  const { page: safePage, totalPages, total, slice } = useMemo(
    () => paginateList(filtered, page, DONOR_DIRECTORY_PAGE_SIZE),
    [filtered, page]
  );

  const valueFilterOptions = useMemo(() => {
    const col = filters.column;
    if (!col) return [];
    return buildValueOptionsFromRows(rows, col, historyCellValue);
  }, [filters.column, rows]);

  const visibleDataColumns = useMemo(
    () => DONATION_HISTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility]
  );

  const tableHeaders = useMemo(
    () => visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
    [visibleDataColumns]
  );

  const getExportData = useCallback(() => {
    const headers = visibleDataColumns.map((c) => c.label);
    const dataRows = filtered.map((row) =>
      visibleDataColumns.map((c) => historyCellValue(c.id, row))
    );
    return { headers, rows: dataRows };
  }, [filtered, visibleDataColumns]);

  const showRows = !loading && slice.length > 0;

  return (
    <GlobalDirectoryShell
      filters={
        <>
          <GlobalDirectoryFiltersSection
            disabled={loading}
            searchLabel="Search"
            searchPlaceholder="Receipt, purpose, status, amount…"
            searchValue={filters.search}
            onSearchChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(1);
            }}
            columnLabel="Column"
            columnValue={filters.column}
            onColumnChange={(v) => {
              setFilters((f) => ({ ...f, column: v, value: "" }));
              setPage(1);
            }}
            gridColumnDefs={DONATION_HISTORY_COLUMN_DEFS}
            valueLabel="Value"
            valueValue={filters.value}
            onValueChange={(v) => {
              setFilters((f) => ({ ...f, value: v }));
              setPage(1);
            }}
            valueOptions={valueFilterOptions}
            onClear={() => {
              setFilters(defaultDirectoryFilters());
              setPage(1);
            }}
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={DONATION_HISTORY_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={DONATION_HISTORY_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getExportData}
                filenameBase="donation_history"
              />
            }
          />
          {error ? (
            <p className="ub-form-alert ub-form-alert--error" role="alert">
              {error}
            </p>
          ) : null}
        </>
      }
      grid={
        <GlobalDirectoryGridSection
          loading={loading}
          loadingMessage="Loading donation history…"
          headers={tableHeaders}
          isEmpty={!loading && filtered.length === 0}
          emptyMessage="No donation history available."
          tableAriaLabel="Donation history directory"
          wrapClassName="ub-table-wrap"
          tableClassName="ub-table"
        >
          {showRows
            ? slice.map((row) => (
                <tr key={row.id}>
                  {visibleDataColumns.map((col) => (
                    <td key={col.id}>{historyCellValue(col.id, row)}</td>
                  ))}
                </tr>
              ))
            : null}
        </GlobalDirectoryGridSection>
      }
      pagination={
        !loading && filtered.length > 0 ? (
          <GlobalDirectoryPaginationSection
            page={safePage}
            totalPages={totalPages}
            total={total}
            pageSize={DONOR_DIRECTORY_PAGE_SIZE}
            onPageChange={setPage}
            label="Donation history pagination"
          />
        ) : null
      }
    />
  );
}
